"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Reveals `target` at a controlled, per-frame pace so text "types" out
 * (ChatGPT/Claude-style) regardless of how coarsely the network delivers it.
 * Backends often flush text in a handful of large bursts (streamed reply
 * deltas, STT interim transcripts), so animating raw chunk arrival looks
 * instant — this decouples the on-screen reveal from chunk boundaries.
 *
 * Used by the assistant reply bubble (`AiMessage`) and the live voice
 * transcript bubble (`AiLiveTranscript`).
 *
 * @param enabled Fixed at mount by callers: only freshly-streaming text
 *   animates; history renders in full immediately.
 * @param held Voice sync — delays the START of the reveal (nothing shown yet)
 *   until the agent audibly speaks; an in-progress reveal never pauses.
 * @param pace "speech" tracks read-aloud speed for the agent's voice reply;
 *   "slow" tracks natural talking speed for the live user transcript; "fast"
 *   is the ease-out catch-up used for text chat.
 */
export function useTypewriter(
  target: string,
  enabled: boolean,
  held = false,
  pace: "fast" | "speech" | "slow" = "fast",
): string {
  const [displayed, setDisplayed] = useState(() => (enabled ? "" : target));
  const indexRef = useRef(enabled ? 0 : target.length);
  const targetRef = useRef(target);
  const rafRef = useRef<number | null>(null);
  // Wall-clock of the last revealed char — the timed paces are ms-based, NOT
  // frame-based, so they run identically on 60Hz and 120Hz displays.
  const lastCharAtRef = useRef(0);

  useEffect(() => {
    targetRef.current = target;

    if (!enabled) {
      indexRef.current = target.length;
      setDisplayed(target);
      return;
    }

    // Voice sync: hold the START of the reveal until the agent audibly speaks.
    // Only the start — once revealing, a mid-reply silence never pauses it.
    if (held && indexRef.current === 0) return;

    // Guard against a target that shrank (e.g. authoritative content replacing
    // longer accumulated deltas): never reveal past what exists.
    if (indexRef.current > target.length) {
      indexRef.current = target.length;
      setDisplayed(target.slice(0, indexRef.current));
    }

    const tick = (now: number) => {
      const full = targetRef.current;
      const remaining = full.length - indexRef.current;
      if (remaining <= 0) {
        rafRef.current = null;
        return;
      }
      let step: number;
      if (pace === "speech" || pace === "slow") {
        // Steady timed cadence: "speech" ≈ 30 chars/s (read-aloud), "slow"
        // ≈ 10 chars/s (natural talking speed). Each doubles while a long
        // backlog remains so the text never trails the voice by more than a
        // beat. Elapsed time is clamped so a paused reveal (voice hold, tab
        // in background) can't dump a paragraph on resume.
        const msPerChar = pace === "slow" ? 100 : 33;
        const catchUpAt = pace === "slow" ? 80 : 120;
        const interval = remaining > catchUpAt ? msPerChar / 2 : msPerChar;
        const elapsed = Math.min(now - lastCharAtRef.current, interval * 3);
        step = Math.floor(elapsed / interval);
        if (step > 0) lastCharAtRef.current = now;
      } else {
        // Ease-out: catch up fast on big bursts, still visibly type small ones.
        step = Math.max(1, Math.ceil(remaining / 8));
      }
      if (step > 0) {
        indexRef.current += step;
        setDisplayed(full.slice(0, indexRef.current));
      }
      rafRef.current = requestAnimationFrame(tick);
    };

    if (rafRef.current == null && indexRef.current < target.length) {
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (rafRef.current != null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [target, enabled, held, pace]);

  return displayed;
}
